/**
 * Centralized date utility wrapper using Day.js
 * Replaces deprecated Moment.js with modern, lightweight alternative
 *
 * @module dateUtils
 * @description Production-grade date handling utility following DRY and SRP principles
 */

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isBetween from 'dayjs/plugin/isBetween';
import duration from 'dayjs/plugin/duration';

// Load Day.js plugins
dayjs.extend(customParseFormat);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(duration);

/**
 * Export configured dayjs instance as default
 */
export default dayjs;

/**
 * Format a date to a specific string format
 * @param {Date|string|number|dayjs.Dayjs} date - The date to format
 * @param {string} format - The format string (default: 'YYYY-MM-DD')
 * @returns {string} Formatted date string
 */
export const formatToDateString = (date, format = 'YYYY-MM-DD') => {
  return dayjs(date).format(format);
};

/**
 * Calculate age from a birth date
 * @param {Date|string|number|dayjs.Dayjs} birthDate - The birth date
 * @returns {number} Age in years
 */
export const calculateAge = (birthDate) => {
  return dayjs().diff(dayjs(birthDate), 'year');
};

/**
 * Get relative time from now (e.g., "2 hours ago")
 * @param {Date|string|number|dayjs.Dayjs} date - The date
 * @returns {string} Relative time string
 */
export const fromNow = (date) => {
  return dayjs(date).fromNow();
};

/**
 * Get relative time to now (e.g., "in 2 hours")
 * @param {Date|string|number|dayjs.Dayjs} date - The date
 * @returns {string} Relative time string
 */
export const toNow = (date) => {
  return dayjs(date).toNow();
};

/**
 * Check if a date is valid
 * @param {Date|string|number|dayjs.Dayjs} date - The date to validate
 * @returns {boolean} True if valid, false otherwise
 */
export const isValid = (date) => {
  return dayjs(date).isValid();
};

/**
 * Get start of a time unit (day, month, year, etc.)
 * @param {Date|string|number|dayjs.Dayjs} date - The date
 * @param {string} unit - The time unit (default: 'day')
 * @returns {dayjs.Dayjs} Day.js object at start of unit
 */
export const startOf = (date, unit = 'day') => {
  return dayjs(date).startOf(unit);
};

/**
 * Get end of a time unit (day, month, year, etc.)
 * @param {Date|string|number|dayjs.Dayjs} date - The date
 * @param {string} unit - The time unit (default: 'day')
 * @returns {dayjs.Dayjs} Day.js object at end of unit
 */
export const endOf = (date, unit = 'day') => {
  return dayjs(date).endOf(unit);
};

/**
 * Add time to a date
 * @param {Date|string|number|dayjs.Dayjs} date - The date
 * @param {number} value - The value to add
 * @param {string} unit - The time unit (default: 'day')
 * @returns {dayjs.Dayjs} New Day.js object with added time
 */
export const addTime = (date, value, unit = 'day') => {
  return dayjs(date).add(value, unit);
};

/**
 * Subtract time from a date
 * @param {Date|string|number|dayjs.Dayjs} date - The date
 * @param {number} value - The value to subtract
 * @param {string} unit - The time unit (default: 'day')
 * @returns {dayjs.Dayjs} New Day.js object with subtracted time
 */
export const subtractTime = (date, value, unit = 'day') => {
  return dayjs(date).subtract(value, unit);
};

/**
 * Check if date is before another date
 * @param {Date|string|number|dayjs.Dayjs} date1 - First date
 * @param {Date|string|number|dayjs.Dayjs} date2 - Second date
 * @returns {boolean} True if date1 is before date2
 */
export const isBefore = (date1, date2) => {
  return dayjs(date1).isBefore(dayjs(date2));
};

/**
 * Check if date is after another date
 * @param {Date|string|number|dayjs.Dayjs} date1 - First date
 * @param {Date|string|number|dayjs.Dayjs} date2 - Second date
 * @returns {boolean} True if date1 is after date2
 */
export const isAfter = (date1, date2) => {
  return dayjs(date1).isAfter(dayjs(date2));
};

/**
 * Check if date is same as another date
 * @param {Date|string|number|dayjs.Dayjs} date1 - First date
 * @param {Date|string|number|dayjs.Dayjs} date2 - Second date
 * @param {string} unit - The time unit for comparison (default: 'day')
 * @returns {boolean} True if dates are the same
 */
export const isSame = (date1, date2, unit = 'day') => {
  return dayjs(date1).isSame(dayjs(date2), unit);
};

/**
 * Get current date/time
 * @returns {dayjs.Dayjs} Current Day.js object
 */
export const now = () => {
  return dayjs();
};

/**
 * Parse a date string with a specific format
 * @param {string} dateString - The date string to parse
 * @param {string} format - The format of the date string
 * @returns {dayjs.Dayjs} Parsed Day.js object
 */
export const parseDate = (dateString, format) => {
  return dayjs(dateString, format);
};

/**
 * Convert to UTC
 * @param {Date|string|number|dayjs.Dayjs} date - The date
 * @returns {dayjs.Dayjs} UTC Day.js object
 */
export const toUTC = (date) => {
  return dayjs(date).utc();
};

/**
 * Get difference between two dates
 * @param {Date|string|number|dayjs.Dayjs} date1 - First date
 * @param {Date|string|number|dayjs.Dayjs} date2 - Second date
 * @param {string} unit - The time unit (default: 'day')
 * @returns {number} Difference in specified unit
 */
export const getDifference = (date1, date2, unit = 'day') => {
  return dayjs(date1).diff(dayjs(date2), unit);
};
